import type { Ref } from 'react';
import { Plus, Trash2, Copy } from 'lucide-react';
import type { AdDefinition } from '@/types';

type Props = {
  currentAds: AdDefinition[];
  activeAdIndex: number;
  setActiveAdIndex: (n: number) => void;
  adCount: number;
  isMultiAd: boolean;
  showCopyMenu: boolean;
  setShowCopyMenu: (v: boolean) => void;
  copyMenuRef: Ref<HTMLDivElement>;
  addAd: (copyFromIndex?: number) => void;
  removeAd: (adIndex: number) => void;
};

export function AdBar({
  currentAds, activeAdIndex, setActiveAdIndex, adCount, isMultiAd,
  showCopyMenu, setShowCopyMenu, copyMenuRef, addAd, removeAd,
}: Props) {
  return (
    <div className="flex items-center gap-2 mb-3 px-1">
      <div className="flex items-center gap-1 bg-muted/30 rounded-md p-0.5 border border-border/40">
        {currentAds.map(ad => (
          <button
            key={ad.ad_index}
            onClick={() => setActiveAdIndex(ad.ad_index)}
            className={`px-3 py-1 text-xs font-mono rounded transition-all ${
              activeAdIndex === ad.ad_index
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
          >
            Ad {ad.ad_index}
            {ad.headlines.some(s => s.variants.some(v => v.template_string.trim())) && (
              <span className="ml-1 opacity-50">·</span>
            )}
          </button>
        ))}
      </div>

      {adCount < 3 && (
        <div className="relative" ref={copyMenuRef}>
          <button
            onClick={() => setShowCopyMenu(!showCopyMenu)}
            className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-muted-foreground/50 hover:text-primary border border-dashed border-border/40 hover:border-primary/40 rounded transition-colors"
          >
            <Plus className="size-3" /> Add ad
          </button>
          {showCopyMenu && (
            <div className="absolute top-full left-0 mt-1 bg-popover border border-border rounded-md shadow-lg z-50 py-1 min-w-[160px]">
              <button
                onClick={() => { addAd(); setShowCopyMenu(false); }}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted/50 transition-colors"
              >
                Start from scratch
              </button>
              {currentAds.map(ad => (
                <button
                  key={ad.ad_index}
                  onClick={() => { addAd(ad.ad_index); setShowCopyMenu(false); }}
                  className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted/50 transition-colors flex items-center gap-1.5"
                >
                  <Copy className="size-3 text-muted-foreground/50" />
                  Copy from Ad {ad.ad_index}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {isMultiAd && activeAdIndex > 1 && (
        <button
          onClick={() => removeAd(activeAdIndex)}
          className="ml-auto flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-muted-foreground/40 hover:text-destructive border border-border/30 hover:border-destructive/40 rounded transition-colors"
        >
          <Trash2 className="size-3" /> Remove Ad {activeAdIndex}
        </button>
      )}
    </div>
  );
}
